import React, { useState } from 'react';
import './GameGeneration.css';
import { getAllAIPlayers } from '../config/ai-players-config';
import { getCompatiblePatterns } from '../config/patterns';
import { calculateAIMove, getPlayerId, STRATEGIES } from '../config/oracle-ai';
import { checkWin } from '../config/patterns';

const GameGeneration = ({ onBackToGame }) => {
  const [generationConfig, setGenerationConfig] = useState({
    gamesPerCombination: 10,
    boardSizes: [3, 4, 5, 6, 7, 8],
    patterns: ['tic'],
    aiPlayerIds: ['alfred', 'prof-corey', 'wanda', 'erdos', 'harary', 'lovelace', 'katherine'],
    strategies: [STRATEGIES.AI, STRATEGIES.RANDOM, STRATEGIES.GARDNER],
    includePlayerVsAI: true,
    includeAIVsAI: true,
    exportFormat: 'csv',
    fastMode: true
  });
  
  const [isRunning, setIsRunning] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0, description: '' });
  const [results, setResults] = useState([]);
  const [csvData, setCsvData] = useState('');

  const aiPlayers = getAllAIPlayers();

  // Get compatible patterns for the selected board sizes
  const getCompatiblePatternsForSizes = (boardSizes) => {
    const allPatterns = ['skinny', 'boxy', 'ellie', 'knobby', 'snooty', 'snakey', 'tic'];
    const compatiblePatterns = [];
    
    for (const pattern of allPatterns) {
      // Check if pattern is compatible with any of the selected board sizes
      const isCompatible = boardSizes.some(size => {
        const compatibleForSize = getCompatiblePatterns(size);
        return compatibleForSize.some(p => p.id === pattern);
      });
      
      if (isCompatible) {
        compatiblePatterns.push(pattern);
      }
    }
    
    return compatiblePatterns;
  };

  const handleConfigChange = (field, value) => {
    setGenerationConfig(prev => {
      const newConfig = {
        ...prev,
        [field]: value
      };
      
      // If board sizes changed, update patterns to only include compatible ones
      if (field === 'boardSizes') {
        const compatiblePatterns = getCompatiblePatternsForSizes(value);
        // Keep only patterns that are compatible with the new board sizes
        newConfig.patterns = newConfig.patterns.filter(pattern => 
          compatiblePatterns.includes(pattern)
        );
        
        // If no patterns are selected, select all compatible ones
        if (newConfig.patterns.length === 0 && compatiblePatterns.length > 0) {
          newConfig.patterns = compatiblePatterns;
        }
      }
      
      return newConfig;
    });
  };

  const initializeBoard = (size) => {
    return Array(size).fill(null).map(() => Array(size).fill(null));
  };

  const checkDraw = (board) => {
    return board.every(row => row.every(cell => cell !== null));
  };

  const runSingleGame = async (board, pattern, player1Id, player2Id, strategy, boardSize, gameMode) => {
    let currentBoard = board.map(row => [...row]);
    let currentPlayer = 'X';
    let moveHistory = [];
    let moveNumber = 0;
    const maxMoves = boardSize * boardSize; // Prevent infinite loops

    while (moveNumber < maxMoves) {
      moveNumber++;
      
      // Determine AI player ID for current player
      let playerId;
      if (gameMode === 'player-computer') {
        playerId = currentPlayer === 'O' ? getPlayerId(player1Id) : null;
      } else {
        playerId = currentPlayer === 'X' ? getPlayerId(player1Id) : getPlayerId(player2Id);
      }
      
      // Calculate AI move
      let aiMove;
      if (playerId === null) {
        // For player vs AI, simulate human moves (random for data collection)
        const availableMoves = getAvailableMovesFast(currentBoard, boardSize);
        if (availableMoves.length === 0) {
          return { result: 'draw', moves: moveHistory, moveCount: moveNumber };
        }
        aiMove = availableMoves[Math.floor(Math.random() * availableMoves.length)];
      } else {
        aiMove = await calculateAIMove(currentBoard, pattern, playerId, boardSize, strategy, false);
      }
      
      if (!aiMove) {
        return { result: 'draw', moves: moveHistory, moveCount: moveNumber };
      }

      // Record the move (simplified for speed)
      moveHistory.push({
        moveNumber,
        player: currentPlayer,
        row: aiMove.row,
        col: aiMove.col,
        playerId: playerId
      });

      // Make the move
      currentBoard[aiMove.row][aiMove.col] = currentPlayer;

      // Check for win (simplified check for speed)
      const winResult = checkWinFast(currentBoard, pattern, currentPlayer, boardSize);
      if (winResult) {
        return { 
          result: currentPlayer === 'X' ? 'player1' : 'player2', 
          moves: moveHistory, 
          moveCount: moveNumber,
          winningPositions: winResult.positions
        };
      }

      // Check for draw
      if (moveNumber >= maxMoves) {
        return { result: 'draw', moves: moveHistory, moveCount: moveNumber };
      }

      // Switch players
      currentPlayer = currentPlayer === 'X' ? 'O' : 'X';
    }
    
    return { result: 'draw', moves: moveHistory, moveCount: moveNumber };
  };

  const getAvailableMoves = (board, boardSize) => {
    const moves = [];
    for (let row = 0; row < boardSize; row++) {
      for (let col = 0; col < boardSize; col++) {
        if (board[row][col] === null) {
          moves.push({ row, col });
        }
      }
    }
    return moves;
  };

  // Optimized version for speed
  const getAvailableMovesFast = (board, boardSize) => {
    const moves = [];
    for (let row = 0; row < boardSize; row++) {
      for (let col = 0; col < boardSize; col++) {
        if (board[row][col] === null) {
          moves.push({ row, col });
        }
      }
    }
    return moves;
  };

  // Simplified win check for speed
  const checkWinFast = (board, pattern, player, boardSize) => {
    // Use the original checkWin but with simplified logic for common patterns
    if (pattern === 'skinny') {
      // Fast check for skinny (line of 4)
      return checkSkinnyFast(board, player, boardSize);
    }
    if (pattern === 'tic') {
      // Fast check for tic (3-in-a-row)
      return checkTicFast(board, player, boardSize);
    }
    // Fall back to original checkWin for other patterns
    return checkWin(board, pattern, player, boardSize);
  };

  // Fast skinny pattern check - use comprehensive checking
  const checkSkinnyFast = (board, player, boardSize) => {
    return checkWin(board, 'skinny', player, boardSize);
  };

  // Fast tic pattern check - use comprehensive checking
  const checkTicFast = (board, player, boardSize) => {
    return checkWin(board, 'tic', player, boardSize);
  };

  const generateGames = async () => {
    setIsRunning(true);
    setProgress({ current: 0, total: 0, description: 'Calculating total games...' });
    setResults([]);
    setCsvData('');

    // Track performance metrics
    const startTime = Date.now();
    let lastUpdateTime = startTime;
    let gamesPerSecond = 0;

    // Calculate total combinations
    let totalCombinations = 0;
    const combinations = [];

    if (generationConfig.includePlayerVsAI) {
      for (const boardSize of generationConfig.boardSizes) {
        const compatiblePatterns = getCompatiblePatterns(boardSize);
        for (const pattern of generationConfig.patterns) {
          if (compatiblePatterns.some(p => p.id === pattern)) {
            for (const playerId of generationConfig.aiPlayerIds) {
              for (const strategy of generationConfig.strategies) {
                totalCombinations += generationConfig.gamesPerCombination;
                combinations.push({
                  boardSize,
                  pattern,
                  player1Id: 'human',
                  player2Id: playerId,
                  strategy: strategy,
                  gameMode: 'player-computer'
                });
              }
            }
          }
        }
      }
    }

    if (generationConfig.includeAIVsAI) {
      for (const boardSize of generationConfig.boardSizes) {
        const compatiblePatterns = getCompatiblePatterns(boardSize);
        for (const pattern of generationConfig.patterns) {
          if (compatiblePatterns.some(p => p.id === pattern)) {
            for (const player1Id of generationConfig.aiPlayerIds) {
              for (const player2Id of generationConfig.aiPlayerIds) {
                for (const strategy of generationConfig.strategies) {
                  totalCombinations += generationConfig.gamesPerCombination;
                  combinations.push({
                    boardSize,
                    pattern,
                    player1Id,
                    player2Id,
                    strategy: strategy,
                    gameMode: 'computer-computer'
                  });
                }
              }
            }
          }
        }
      }
    }

    setProgress({ current: 0, total: totalCombinations, description: 'Starting game generation...' });

    const allResults = [];
    let currentGame = 0;

    for (const combination of combinations) {
      for (let game = 0; game < generationConfig.gamesPerCombination; game++) {
        currentGame++;
        
        // Update progress less frequently for better performance
        if (currentGame % 50 === 0 || currentGame === totalCombinations) {
          // Calculate games per second
          const currentTime = Date.now();
          const elapsedSeconds = (currentTime - startTime) / 1000;
          gamesPerSecond = currentGame / elapsedSeconds;
          
          setProgress({ 
            current: currentGame, 
            total: totalCombinations, 
            description: `Running game ${currentGame}/${totalCombinations}: ${combination.boardSize}x${combination.boardSize} ${combination.pattern} ${combination.player1Id} vs ${combination.player2Id} (${gamesPerSecond.toFixed(1)} games/sec)`
          });
        }

        const board = initializeBoard(combination.boardSize);
        const gameResult = await runSingleGame(
          board,
          combination.pattern,
          combination.player1Id,
          combination.player2Id,
          combination.strategy,
          combination.boardSize,
          combination.gameMode
        );

        // Create simplified result record for speed
        const resultRecord = {
          gameId: currentGame,
          boardSize: combination.boardSize,
          pattern: combination.pattern,
          player1Id: combination.player1Id,
          player2Id: combination.player2Id,
          strategy: combination.strategy,
          gameMode: combination.gameMode,
          result: gameResult.result,
          moveCount: gameResult.moveCount,
          winner: gameResult.result === 'player1' ? combination.player1Id : 
                 gameResult.result === 'player2' ? combination.player2Id : 'draw',
          totalMoves: gameResult.moves.length,
          firstMoveRow: gameResult.moves[0]?.row || '',
          firstMoveCol: gameResult.moves[0]?.col || '',
          lastMoveRow: gameResult.moves[gameResult.moves.length - 1]?.row || '',
          lastMoveCol: gameResult.moves[gameResult.moves.length - 1]?.col || ''
        };

        allResults.push(resultRecord);

        // Minimal delay only every 100 games
        if (currentGame % 100 === 0) {
          await new Promise(resolve => setTimeout(resolve, 1));
        }
      }
    }

    // Calculate final performance metrics
    const totalTime = (Date.now() - startTime) / 1000;
    const finalGamesPerSecond = totalCombinations / totalTime;
    
    setResults(allResults);
    generateCSV(allResults);
    setIsRunning(false);
    setProgress({ 
      current: totalCombinations, 
      total: totalCombinations, 
      description: `Generation complete! Generated ${totalCombinations} games in ${totalTime.toFixed(1)} seconds (${finalGamesPerSecond.toFixed(1)} games/sec)`
    });
  };

  const generateCSV = (results) => {
    if (results.length === 0) return;

    // CSV headers
    const headers = [
      'gameId',
      'boardSize',
      'pattern',
      'player1Id',
      'player2Id',
      'strategy',
      'gameMode',
      'result',
      'moveCount',
      'winner',
      'totalMoves',
      'firstMoveRow',
      'firstMoveCol',
      'lastMoveRow',
      'lastMoveCol'
    ];

    // CSV rows
    const csvRows = results.map(result => {
      return [
        result.gameId,
        result.boardSize,
        result.pattern,
        result.player1Id,
        result.player2Id,
        result.strategy,
        result.gameMode,
        result.result,
        result.moveCount,
        result.winner,
        result.totalMoves,
        result.firstMoveRow,
        result.firstMoveCol,
        result.lastMoveRow,
        result.lastMoveCol
      ].join(',');
    });

    const csvContent = [headers.join(','), ...csvRows].join('\n');
    setCsvData(csvContent);
  };

  const downloadCSV = () => {
    if (!csvData) return;

    const blob = new Blob([csvData], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `harary_game_data_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const getCompatiblePatternsForSize = (boardSize) => {
    return getCompatiblePatterns(boardSize).map(p => p.id);
  };

  return (
    <div className="game-generation">
      <div className="generation-header">
        <h2>Test Game Generation</h2>
        <button onClick={onBackToGame} className="back-button">
          ← Back to Game
        </button>
      </div>

      <div className="generation-container">
        <div className="generation-config">
          <h3>Generation Configuration</h3>
          
          <div className="config-grid">
            <div className="config-item">
              <label>Games per combination:</label>
              <input 
                type="number"
                min="1"
                max="100"
                value={generationConfig.gamesPerCombination}
                onChange={(e) => handleConfigChange('gamesPerCombination', parseInt(e.target.value))}
                disabled={isRunning}
              />
            </div>

            <div className="config-item">
              <label>Board Sizes:</label>
              <div className="checkbox-group">
                {[3, 4, 5, 6, 7, 8].map(size => (
                  <label key={size}>
                    <input 
                      type="checkbox"
                      checked={generationConfig.boardSizes.includes(size)}
                      onChange={(e) => {
                        const newSizes = e.target.checked 
                          ? [...generationConfig.boardSizes, size]
                          : generationConfig.boardSizes.filter(s => s !== size);
                        handleConfigChange('boardSizes', newSizes);
                      }}
                      disabled={isRunning}
                    />
                    {size}x{size}
                  </label>
                ))}
              </div>
            </div>

            <div className="config-item">
              <label>Patterns:</label>
              <div className="checkbox-group">
                {getCompatiblePatternsForSizes(generationConfig.boardSizes).map(pattern => (
                  <label key={pattern}>
                    <input 
                      type="checkbox"
                      checked={generationConfig.patterns.includes(pattern)}
                      onChange={(e) => {
                        const newPatterns = e.target.checked 
                          ? [...generationConfig.patterns, pattern]
                          : generationConfig.patterns.filter(p => p !== pattern);
                        handleConfigChange('patterns', newPatterns);
                      }}
                      disabled={isRunning}
                    />
                    {pattern}
                  </label>
                ))}
              </div>
            </div>

            <div className="config-item">
              <label>AI Players:</label>
              <div className="checkbox-group">
                {aiPlayers.map(player => (
                  <label key={player.id}>
                    <input 
                      type="checkbox"
                      checked={generationConfig.aiPlayerIds.includes(player.id)}
                      onChange={(e) => {
                        const newPlayerIds = e.target.checked 
                          ? [...generationConfig.aiPlayerIds, player.id]
                          : generationConfig.aiPlayerIds.filter(id => id !== player.id);
                        handleConfigChange('aiPlayerIds', newPlayerIds);
                      }}
                      disabled={isRunning}
                    />
                    {player.name} ({player.level})
                  </label>
                ))}
              </div>
            </div>

            <div className="config-item">
              <label>Strategies:</label>
              <div className="checkbox-group">
                <label>
                  <input 
                    type="checkbox"
                    checked={generationConfig.strategies.includes(STRATEGIES.AI)}
                    onChange={(e) => {
                      const newStrategies = e.target.checked 
                        ? [...generationConfig.strategies, STRATEGIES.AI]
                        : generationConfig.strategies.filter(s => s !== STRATEGIES.AI);
                      handleConfigChange('strategies', newStrategies);
                    }}
                    disabled={isRunning}
                  />
                  AI (Random)
                </label>
                <label>
                  <input 
                    type="checkbox"
                    checked={generationConfig.strategies.includes(STRATEGIES.RANDOM)}
                    onChange={(e) => {
                      const newStrategies = e.target.checked 
                        ? [...generationConfig.strategies, STRATEGIES.RANDOM]
                        : generationConfig.strategies.filter(s => s !== STRATEGIES.RANDOM);
                      handleConfigChange('strategies', newStrategies);
                    }}
                    disabled={isRunning}
                  />
                  Random
                </label>
                <label>
                  <input 
                    type="checkbox"
                    checked={generationConfig.strategies.includes(STRATEGIES.GARDNER)}
                    onChange={(e) => {
                      const newStrategies = e.target.checked 
                        ? [...generationConfig.strategies, STRATEGIES.GARDNER]
                        : generationConfig.strategies.filter(s => s !== STRATEGIES.GARDNER);
                      handleConfigChange('strategies', newStrategies);
                    }}
                    disabled={isRunning}
                  />
                  Gardner (Defensive)
                </label>
              </div>
            </div>

            <div className="config-item">
              <label>Game Modes:</label>
              <div className="checkbox-group">
                <label>
                  <input 
                    type="checkbox"
                    checked={generationConfig.includePlayerVsAI}
                    onChange={(e) => handleConfigChange('includePlayerVsAI', e.target.checked)}
                    disabled={isRunning}
                  />
                  Player vs AI
                </label>
                <label>
                  <input 
                    type="checkbox"
                    checked={generationConfig.includeAIVsAI}
                    onChange={(e) => handleConfigChange('includeAIVsAI', e.target.checked)}
                    disabled={isRunning}
                  />
                  AI vs AI
                </label>
              </div>
            </div>

            <div className="config-item">
              <label>Performance Options:</label>
              <div className="checkbox-group">
                <label>
                  <input 
                    type="checkbox"
                    checked={generationConfig.fastMode}
                    onChange={(e) => handleConfigChange('fastMode', e.target.checked)}
                    disabled={isRunning}
                  />
                  Fast Mode (Reduced AI computation for speed)
                </label>
              </div>
            </div>
          </div>

          <button 
            onClick={generateGames} 
            className="generate-button"
            disabled={isRunning}
          >
            {isRunning ? 'Generating Games...' : 'Generate Games'}
          </button>
        </div>

        {isRunning && (
          <div className="generation-progress">
            <h3>Progress</h3>
            <div className="progress-bar">
              <div 
                className="progress-fill" 
                style={{ width: `${(progress.current / progress.total) * 100}%` }}
              ></div>
            </div>
            <p>{progress.description}</p>
            <p>{progress.current} / {progress.total} games completed</p>
          </div>
        )}

        {results.length > 0 && (
          <div className="generation-results">
            <h3>Generation Results</h3>
            <div className="results-summary">
              <p><strong>Total Games Generated:</strong> {results.length}</p>
              <p><strong>CSV Data Size:</strong> {csvData.length} characters</p>
              
              {/* Performance Summary */}
              {progress.description.includes('games/sec') && (
                <div className="performance-summary">
                  <h4>Performance Metrics:</h4>
                  <p className="performance-text">{progress.description}</p>
                </div>
              )}
              
              <div className="result-stats">
                <h4>Result Distribution:</h4>
                <div className="stats-grid">
                  <div className="stat-item">
                    <span className="stat-label">Player 1 Wins:</span>
                    <span className="stat-value">
                      {results.filter(r => r.result === 'player1').length}
                    </span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Player 2 Wins:</span>
                    <span className="stat-value">
                      {results.filter(r => r.result === 'player2').length}
                    </span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Draws:</span>
                    <span className="stat-value">
                      {results.filter(r => r.result === 'draw').length}
                    </span>
                  </div>
                </div>
              </div>

              <button onClick={downloadCSV} className="download-button">
                Download CSV
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GameGeneration;
